import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { apiService } from '../../services/apiService';
import { branding } from '../../constants/branding';
import TradeEvaluationPanel from './TradeEvaluationPanel';
import { formatTradeClosedDisplay, mapTradesForEvaluation, parseFixedOrderAmount } from '../../utils/tradeWindowEvaluation';

// Chart components (we'll use a simple chart library)
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';

interface ProfitAnalytics {
  portfolioOverview: {
    totalInvested: number;
    currentValue: number;
    totalPnL: number;
    totalPnLPercent: number;
    realizedPnL: number;
    unrealizedPnL: number;
    currency: string;
  };
  performanceMetrics: {
    sharpeRatio: number;
    volatility: number;
    winRate: number;
    averageWin: number;
    averageLoss: number;
    profitFactor: number;
    totalTrades: number;
    winningTrades: number;
    losingTrades: number;
  };
  riskMetrics: {
    maxDrawdown: number;
    maxDrawdownPercent: number;
    currentDrawdown: number;
    currentDrawdownPercent: number;
    var95: number;
    cvar95: number;
    beta: number;
    alpha: number;
  };
  botPerformance: Array<{
    botId: number;
    botName: string;
    symbol: string;
    strategy: string;
    allocatedAmount: number;
    currentValue: number;
    pnL: number;
    pnLPercent: number;
    winRate: number;
    totalTrades: number;
    status: string;
  }>;
  allocationBreakdown: Array<{
    botId: number;
    botName: string;
    symbol: string;
    allocatedAmount: number;
    allocationPercent: number;
    currentValue: number;
    pnL: number;
    pnLPercent: number;
  }>;
  equityCurve: Array<{
    timestamp: string;
    value: number;
    pnL: number;
    pnLPercent: number;
  }>;
  benchmarkComparison: {
    benchmarkName: string;
    benchmarkReturn: number;
    portfolioReturn: number;
    alpha: number;
    beta: number;
    correlation: number;
    benchmarkData: Array<{
      timestamp: string;
      value: number;
    }>;
  };
  timeRange: string;
  lastUpdated: string;
}

const ProfitDashboard: React.FC = () => {
  const navigate = useNavigate();
  const profile = useAppSelector((state) => state.profile.profile);
  const [analytics, setAnalytics] = useState<ProfitAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('1M');
  const [activeTab, setActiveTab] = useState('overview');

  // Per-strategy analytics drill-down
  const [expandedBotId, setExpandedBotId] = useState<number | null>(null);
  // dataSource per strategy: 'kucoin' = live KuCoin data, 'db' = original DB rollback
  const [strategyDataSource, setStrategyDataSource] = useState<Record<number, 'kucoin' | 'db'>>({});
  const [strategyAnalytics, setStrategyAnalytics] = useState<Record<number, {
    trades: any[];
    pnlHistory: any[];
    summary: any | null;
    fixedOrderAmount?: number;
    loading: boolean;
    error: string | null;
  }>>({});

  const fetchStrategyOrderSize = async (botId: number): Promise<number | undefined> => {
    try {
      const res = await apiService.get(`/api/retail/trading/strategies/${botId}`);
      if (res.success && res.data) {
        return parseFixedOrderAmount((res.data as { config?: unknown }).config);
      }
    } catch {
      /* optional — panel shows em dash when unknown */
    }
    return undefined;
  };

  const fetchStrategyAnalytics = useCallback(async (botId: number, source: 'kucoin' | 'db') => {
    setStrategyAnalytics(prev => ({
      ...prev,
      [botId]: { trades: [], pnlHistory: [], summary: null, loading: true, error: null }
    }));

    try {
      const fixedOrderAmount = await fetchStrategyOrderSize(botId);

      if (source === 'kucoin') {
        const res = await apiService.getRetailStrategyKucoinAnalytics(botId, 200);
        if (res.success && res.data) {
          const payload = res.data as any;
          const trades = Array.isArray(payload.closedTrades) ? payload.closedTrades.map((t: any) => ({
            symbol: t.symbol,
            side: t.side === 'buy' ? 'LONG' : t.side === 'sell' ? 'SHORT' : (t.side || '').toUpperCase(),
            amount: t.size,
            entryPrice: t.openPrice,
            price: t.closePrice,
            pnl: t.pnl,
            tradeFee: t.tradeFee,
            fundingFee: t.fundingFee,
            leverage: t.leverage,
            closeTime: t.closeTime,
            closeTimeMs: t.closeTimeMs,
          })) : [];
          setStrategyAnalytics(prev => ({
            ...prev,
            [botId]: {
              trades,
              pnlHistory: Array.isArray(payload.pnlHistory) ? payload.pnlHistory : [],
              summary: payload.summary || null,
              fixedOrderAmount,
              loading: false,
              error: null,
            }
          }));
        } else {
          setStrategyAnalytics(prev => ({
            ...prev,
            [botId]: {
              trades: [],
              pnlHistory: [],
              summary: null,
              fixedOrderAmount,
              loading: false,
              error: res.error || 'KuCoin data unavailable',
            }
          }));
        }
      } else {
        const [tradesRes, pnlRes] = await Promise.all([
          apiService.getRetailStrategyTrades(botId, 50),
          apiService.getRetailStrategyPnlHistory(botId, 30)
        ]);
        setStrategyAnalytics(prev => ({
          ...prev,
          [botId]: {
            trades: tradesRes.success && Array.isArray(tradesRes.data) ? tradesRes.data : [],
            pnlHistory: pnlRes.success && Array.isArray(pnlRes.data) ? pnlRes.data : [],
            summary: null,
            fixedOrderAmount,
            loading: false,
            error: null
          }
        }));
      }
    } catch (err) {
      setStrategyAnalytics(prev => ({
        ...prev,
        [botId]: { trades: [], pnlHistory: [], summary: null, loading: false, error: 'Failed to load strategy analytics' }
      }));
    }
  }, []);

  const toggleStrategyExpand = (botId: number) => {
    if (expandedBotId === botId) {
      setExpandedBotId(null);
    } else {
      setExpandedBotId(botId);
      // Default to 'db' until kucoin-analytics backend is deployed to production
      const currentSource = strategyDataSource[botId] ?? 'db';
      const sa = strategyAnalytics[botId];
      if (!sa || (sa.loading === false && sa.trades.length === 0 && !sa.error)) {
        fetchStrategyAnalytics(botId, currentSource);
      }
    }
  };

  const switchStrategySource = (botId: number, source: 'kucoin' | 'db') => {
    setStrategyDataSource(prev => ({ ...prev, [botId]: source }));
    fetchStrategyAnalytics(botId, source);
  };

  const timeRanges = [
    { value: '1D', label: '1 Day' },
    { value: '1W', label: '1 Week' },
    { value: '1M', label: '1 Month' },
    { value: '3M', label: '3 Months' },
    { value: '1Y', label: '1 Year' }
  ];

  const fetchAnalytics = async (timeRange: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const userId = profile?.id;
      if (!userId) {
        console.error('No user ID available for analytics fetch');
        setError('No user ID available');
        setLoading(false);
        return;
      }
      const response = await apiService.get(`/api/retail/trading/analytics/${userId}?timeRange=${timeRange}`);
      
      if (response.success && response.data) {
        setAnalytics(response.data);
      } else {
        setError('Failed to fetch profit analytics');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Error loading profit analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(selectedTimeRange);
  }, [selectedTimeRange]);

  const handleTimeRangeChange = (newTimeRange: string) => {
    setSelectedTimeRange(newTimeRange);
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const getPnLColor = (value: number) => {
    return value >= 0 ? '#4caf50' : '#f44336';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RUNNING': return '#4caf50';
      case 'STOPPED': return '#f44336';
      case 'PAUSED': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading profit analytics...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#f44336'
      }}>
        {error}
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#666'
      }}>
        No analytics data available
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 20 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate('/trading-bots?tab=allocation')}
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            borderRadius: 6,
            border: `1px solid ${branding.borderColor}`,
            backgroundColor: 'white',
            cursor: 'pointer'
          }}
        >
          ← Back to Bot Allocation
        </button>
        <h1 style={{ color: branding.primaryColor, marginBottom: 8 }}>
          Profit Analytics Dashboard
        </h1>
        <p style={{ color: '#666', margin: 0 }}>
          Track your trading bot performance and portfolio analytics
        </p>
      </div>

      {/* Time Range Selector */}
      <div style={{ 
        marginBottom: 24, 
        display: 'flex', 
        gap: 8, 
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        {timeRanges.map(range => (
          <button
            key={range.value}
            onClick={() => handleTimeRangeChange(range.value)}
            style={{
              padding: '8px 16px',
              backgroundColor: selectedTimeRange === range.value ? branding.primaryColor : 'transparent',
              color: selectedTimeRange === range.value ? 'white' : branding.textColor,
              border: `1px solid ${branding.borderColor}`,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: selectedTimeRange === range.value ? 600 : 400
            }}
          >
            {range.label}
          </button>
        ))}
      </div>

      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        borderBottom: `2px solid ${branding.borderColor}`,
        marginBottom: 24,
        overflowX: 'auto'
      }}>
        {[
          { id: 'overview', label: 'Portfolio Overview' },
          { id: 'performance', label: 'Performance Metrics' },
          { id: 'allocation', label: 'Allocation Breakdown' },
          { id: 'equity', label: 'Equity Curve' },
          { id: 'risk', label: 'Risk Analysis' },
          { id: 'benchmark', label: 'Benchmark Comparison' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: activeTab === tab.id ? branding.primaryColor : branding.textColor,
              border: 'none',
              borderBottom: activeTab === tab.id ? `2px solid ${branding.primaryColor}` : '2px solid transparent',
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: activeTab === tab.id ? 600 : 400,
              whiteSpace: 'nowrap'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Portfolio Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Portfolio Summary Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: 20, 
            marginBottom: 32 
          }}>
            <div style={{ 
              padding: 24, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 16, color: '#666' }}>Total Invested</h3>
              <div style={{ fontSize: 28, fontWeight: 600, color: branding.primaryColor }}>
                {formatCurrency(analytics.portfolioOverview.totalInvested)}
              </div>
            </div>

            <div style={{ 
              padding: 24, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 16, color: '#666' }}>Current Value</h3>
              <div style={{ fontSize: 28, fontWeight: 600, color: branding.primaryColor }}>
                {formatCurrency(analytics.portfolioOverview.currentValue)}
              </div>
            </div>

            <div style={{ 
              padding: 24, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 16, color: '#666' }}>Total P&L</h3>
              <div style={{ 
                fontSize: 28, 
                fontWeight: 600, 
                color: getPnLColor(analytics.portfolioOverview.totalPnL)
              }}>
                {formatCurrency(analytics.portfolioOverview.totalPnL)}
              </div>
              <div style={{ 
                fontSize: 16, 
                color: getPnLColor(analytics.portfolioOverview.totalPnLPercent),
                marginTop: 4
              }}>
                {formatPercentage(analytics.portfolioOverview.totalPnLPercent)}
              </div>
            </div>

            <div style={{ 
              padding: 24, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 16, color: '#666' }}>Realized P&L</h3>
              <div style={{ 
                fontSize: 24, 
                fontWeight: 600, 
                color: getPnLColor(analytics.portfolioOverview.realizedPnL)
              }}>
                {formatCurrency(analytics.portfolioOverview.realizedPnL)}
              </div>
              <div style={{ fontSize: 14, color: '#666', marginTop: 8 }}>
                Unrealized: {formatCurrency(analytics.portfolioOverview.unrealizedPnL)}
              </div>
            </div>
          </div>

          {/* Individual Bot Performance */}
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: 12, 
            border: `1px solid ${branding.borderColor}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            padding: 24
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: 20 }}>Individual Bot Performance</h3>
            <div style={{ display: 'grid', gap: 16 }}>
              {analytics.botPerformance.map(bot => {
                const isExpanded = expandedBotId === bot.botId;
                const source = strategyDataSource[bot.botId] || 'kucoin';
                const sa = strategyAnalytics[bot.botId];
                return (
                  <div key={bot.botId} style={{ 
                    border: `1px solid ${isExpanded ? '#0d6efd' : branding.borderColor}`, 
                    borderRadius: 8,
                    backgroundColor: '#fafafa',
                    overflow: 'hidden'
                  }}>
                    {/* Bot summary row */}
                    <div style={{ padding: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: 18 }}>{bot.botName}</h4>
                          <div style={{ fontSize: 14, color: '#666' }}>
                            {bot.symbol} • {bot.strategy}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ 
                            padding: '4px 12px', 
                            borderRadius: 4, 
                            fontSize: 12,
                            backgroundColor: getStatusColor(bot.status) + '20',
                            color: getStatusColor(bot.status),
                            fontWeight: 600
                          }}>
                            {bot.status}
                          </span>
                          <button
                            onClick={() => toggleStrategyExpand(bot.botId)}
                            style={{
                              padding: '5px 12px', borderRadius: 6, border: `1px solid ${isExpanded ? '#0d6efd' : '#dee2e6'}`,
                              background: isExpanded ? '#0d6efd' : '#fff', color: isExpanded ? '#fff' : '#495057',
                              cursor: 'pointer', fontSize: 12, fontWeight: 600
                            }}
                          >
                            {isExpanded ? '▲ Hide Analytics' : '▼ View Analytics'}
                          </button>
                        </div>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16 }}>
                        <div>
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Allocated</div>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>{formatCurrency(bot.allocatedAmount)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Current Value</div>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>{formatCurrency(bot.currentValue)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>P&L</div>
                          <div style={{ fontSize: 16, fontWeight: 600, color: getPnLColor(bot.pnL) }}>{formatCurrency(bot.pnL)}</div>
                          <div style={{ fontSize: 12, color: getPnLColor(bot.pnLPercent) }}>{formatPercentage(bot.pnLPercent)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Win Rate</div>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>{bot.winRate.toFixed(1)}%</div>
                          <div style={{ fontSize: 12, color: '#666' }}>{bot.totalTrades} trades</div>
                        </div>
                      </div>
                    </div>

                    {/* Expandable analytics drill-down */}
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid #e9ecef', background: '#fff', padding: 16 }}>
                        {/* Data source toggle */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                          <span style={{ fontSize: 12, color: '#6c757d', fontWeight: 500 }}>Data source:</span>
                          <div style={{ display: 'inline-flex', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 6, padding: 3, gap: 2 }}>
                            <button
                              onClick={() => switchStrategySource(bot.botId, 'kucoin')}
                              title="Live data from KuCoin exchange (more accurate)"
                              style={{
                                padding: '3px 10px', borderRadius: 4, border: 0, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                background: source === 'kucoin' ? '#0d6efd' : 'transparent',
                                color: source === 'kucoin' ? '#fff' : '#495057'
                              }}
                            >KuCoin Live</button>
                            <button
                              onClick={() => switchStrategySource(bot.botId, 'db')}
                              title="Rollback to original DB data"
                              style={{
                                padding: '3px 10px', borderRadius: 4, border: 0, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                background: source === 'db' ? '#6c757d' : 'transparent',
                                color: source === 'db' ? '#fff' : '#495057'
                              }}
                            >DB (Rollback)</button>
                          </div>
                        </div>

                        {sa?.loading && (
                          <div style={{ textAlign: 'center', padding: '20px 0', color: '#6c757d', fontSize: 13 }}>Loading analytics...</div>
                        )}

                        {sa?.error && (
                          <div style={{ padding: '10px 14px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, marginBottom: 12, fontSize: 13, color: '#856404', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>⚠️ {sa.error}</span>
                            <button onClick={() => switchStrategySource(bot.botId, 'db')} style={{ marginLeft: 10, padding: '2px 8px', border: '1px solid #856404', borderRadius: 4, background: 'transparent', color: '#856404', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Use DB</button>
                          </div>
                        )}

                        {sa && !sa.loading && !sa.error && (
                          <>
                            {/* KuCoin summary bar */}
                            {source === 'kucoin' && sa.summary && (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8, background: '#eaf4ff', border: '1px solid #b8d4f0', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 12 }}>
                                {[
                                  { label: 'Closed', value: sa.summary.closedPositionCount },
                                  { label: 'Gross P&L', value: `${Number(sa.summary.grossPnl ?? sa.summary.totalPnl) >= 0 ? '+' : ''}$${Number(sa.summary.grossPnl ?? sa.summary.totalPnl).toFixed(4)}`, color: Number(sa.summary.grossPnl ?? sa.summary.totalPnl) >= 0 ? '#198754' : '#dc3545' },
                                  { label: 'Net P&L', value: `${Number(sa.summary.netPnlAfterFees ?? sa.summary.totalPnl) >= 0 ? '+' : ''}$${Number(sa.summary.netPnlAfterFees ?? sa.summary.totalPnl).toFixed(4)}`, color: Number(sa.summary.netPnlAfterFees ?? sa.summary.totalPnl) >= 0 ? '#198754' : '#dc3545' },
                                  { label: 'Trade Fees', value: `-$${Math.abs(Number(sa.summary.totalTradeFee)).toFixed(4)}`, color: '#dc3545' },
                                  { label: 'Win Rate', value: `${Number(sa.summary.winRate).toFixed(1)}%`, color: '#0d6efd' },
                                  { label: 'W / L', value: `${sa.summary.winCount} / ${sa.summary.lossCount}` },
                                ].map((item, i) => (
                                  <div key={i}>
                                    <div style={{ color: '#5c7fa5', fontWeight: 500, marginBottom: 2 }}>{item.label}</div>
                                    <div style={{ fontWeight: 700, color: (item as any).color || '#212529', fontSize: 13 }}>{item.value}</div>
                                  </div>
                                ))}
                                <div>
                                  <div style={{ color: '#5c7fa5', fontWeight: 500, marginBottom: 2 }}>Source</div>
                                  <div style={{ display: 'inline-block', padding: '1px 6px', background: '#0d6efd', color: '#fff', borderRadius: 4, fontWeight: 600, fontSize: 10 }}>🔴 LIVE</div>
                                </div>
                              </div>
                            )}

                            {!sa.loading && (
                              <TradeEvaluationPanel
                                trades={mapTradesForEvaluation(sa.trades, source)}
                                fixedOrderAmount={sa.fixedOrderAmount}
                                defaultCount={12}
                              />
                            )}

                            {/* Trades table */}
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: '#212529', marginBottom: 8 }}>
                                {source === 'kucoin' ? 'Closed Trades (KuCoin)' : 'Recent Trades (DB)'}
                              </div>
                              <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Symbol / Side</th>
                                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>
                                        {source === 'kucoin' ? 'Size @ Entry → Exit' : 'Amount @ Price'}
                                      </th>
                                      {source === 'kucoin' && (
                                        <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Fees</th>
                                      )}
                                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>P&L</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sa.trades.length > 0 ? sa.trades.map((t: any, idx: number) => {
                                      const pnl = Number(t.pnl || 0);
                                      const totalFee = source === 'kucoin' ? Math.abs(Number(t.tradeFee || 0)) + Math.abs(Number(t.fundingFee || 0)) : 0;
                                      return (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f3f5' }}>
                                          <td style={{ padding: '6px 8px', color: '#212529' }}>
                                            <span style={{ fontWeight: 600 }}>{t.symbol}</span>{' '}
                                            <span style={{ color: t.side === 'LONG' || t.side === 'buy' ? '#198754' : '#dc3545', fontWeight: 600 }}>{t.side}</span>
                                            {(t.closeTimeMs != null || t.closeTime || t.closedAt || t.timestamp) && (
                                              <div style={{ fontSize: 10, color: '#adb5bd' }}>
                                                {formatTradeClosedDisplay(t)}
                                              </div>
                                            )}
                                          </td>
                                          <td style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d' }}>
                                            {source === 'kucoin'
                                              ? `${t.amount} @ $${Number(t.entryPrice || 0).toFixed(4)} → $${Number(t.price || 0).toFixed(4)}`
                                              : `$${Number(t.amount || 0).toFixed(2)} @ $${Number(t.price || 0).toFixed(4)}`}
                                          </td>
                                          {source === 'kucoin' && (
                                            <td style={{ textAlign: 'right', padding: '6px 8px', color: '#dc3545' }}>
                                              {totalFee > 0 ? `-$${totalFee.toFixed(4)}` : '—'}
                                            </td>
                                          )}
                                          <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: pnl >= 0 ? '#198754' : '#dc3545' }}>
                                            {(pnl >= 0 ? '+' : '') + formatCurrency(pnl)}
                                          </td>
                                        </tr>
                                      );
                                    }) : (
                                      <tr>
                                        <td colSpan={source === 'kucoin' ? 4 : 3} style={{ padding: '12px 8px', textAlign: 'center', color: '#6c757d', fontSize: 12 }}>
                                          {source === 'kucoin' ? 'No closed trades found in KuCoin for this strategy' : 'No trades yet'}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* PnL history mini-chart */}
                            {sa.pnlHistory.length > 0 && (
                              <div>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#212529', marginBottom: 8 }}>
                                  {source === 'kucoin' ? 'P&L History (KuCoin)' : '30-Day P&L History (DB)'}
                                </div>
                                <div style={{ height: 140 }}>
                                  <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={sa.pnlHistory.map((d: any) => ({ ...d, value: Number(d.cumulativePnl || d.pnl || 0) }))}>
                                      <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6c757d' }}
                                        tickFormatter={(v: string) => { const d = new Date(v); return `${d.getMonth()+1}/${d.getDate()}`; }} />
                                      <YAxis tick={{ fontSize: 10, fill: '#6c757d' }} tickFormatter={(v: number) => `$${v.toFixed(0)}`} />
                                      <Tooltip formatter={(v: number) => [formatCurrency(v), 'Cumulative P&L']} labelFormatter={(v: string) => v} />
                                      <Line type="monotone" dataKey="value" stroke={branding.primaryColor} strokeWidth={2} dot={false} />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>
                                <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600, marginTop: 4,
                                  color: Number(sa.pnlHistory[sa.pnlHistory.length - 1]?.cumulativePnl || 0) >= 0 ? '#198754' : '#dc3545' }}>
                                  Total: {formatCurrency(Number(sa.pnlHistory[sa.pnlHistory.length - 1]?.cumulativePnl || 0))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Performance Metrics Tab */}
      {activeTab === 'performance' && (
        <div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: 20, 
            marginBottom: 32 
          }}>
            <div style={{ 
              padding: 20, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Sharpe Ratio</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: branding.primaryColor }}>
                {analytics.performanceMetrics.sharpeRatio.toFixed(2)}
              </div>
            </div>
            
            <div style={{ 
              padding: 20, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Volatility</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#ff9800' }}>
                {analytics.performanceMetrics.volatility.toFixed(1)}%
              </div>
            </div>
            
            <div style={{ 
              padding: 20, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Win Rate</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#4caf50' }}>
                {analytics.performanceMetrics.winRate.toFixed(1)}%
              </div>
            </div>
            
            <div style={{ 
              padding: 20, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Profit Factor</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: branding.primaryColor }}>
                {analytics.performanceMetrics.profitFactor.toFixed(2)}
              </div>
            </div>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: 20 
          }}>
            <div style={{ 
              padding: 24, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`
            }}>
              <h4 style={{ margin: '0 0 16px 0' }}>Trade Statistics</h4>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Trades:</span>
                  <span style={{ fontWeight: 600 }}>{analytics.performanceMetrics.totalTrades}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Winning Trades:</span>
                  <span style={{ fontWeight: 600, color: '#4caf50' }}>
                    {analytics.performanceMetrics.winningTrades}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Losing Trades:</span>
                  <span style={{ fontWeight: 600, color: '#f44336' }}>
                    {analytics.performanceMetrics.losingTrades}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Average Win:</span>
                  <span style={{ fontWeight: 600, color: '#4caf50' }}>
                    {formatCurrency(analytics.performanceMetrics.averageWin)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Average Loss:</span>
                  <span style={{ fontWeight: 600, color: '#f44336' }}>
                    {formatCurrency(analytics.performanceMetrics.averageLoss)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Allocation Breakdown Tab */}
      {activeTab === 'allocation' && (
        <div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
            gap: 24 
          }}>
            {/* Pie Chart */}
            <div style={{ 
              padding: 24, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 20px 0' }}>Allocation Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={analytics.allocationBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }: any) => `${name} ${((percent as number || 0) * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="allocatedAmount"
                  >
                    {analytics.allocationBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Allocation Details */}
            <div style={{ 
              padding: 24, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 20px 0' }}>Allocation Details</h3>
              <div style={{ display: 'grid', gap: 16 }}>
                {analytics.allocationBreakdown.map(allocation => (
                  <div key={allocation.botId} style={{ 
                    padding: 16, 
                    border: `1px solid ${branding.borderColor}`, 
                    borderRadius: 8,
                    backgroundColor: '#fafafa'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <h4 style={{ margin: 0, fontSize: 16 }}>{allocation.botName}</h4>
                      <span style={{ 
                        fontSize: 14, 
                        fontWeight: 600,
                        color: getPnLColor(allocation.pnLPercent)
                      }}>
                        {formatPercentage(allocation.pnLPercent)}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>
                      {allocation.symbol}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 12, color: '#666' }}>Allocated</div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>
                          {formatCurrency(allocation.allocatedAmount)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#666' }}>Current Value</div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>
                          {formatCurrency(allocation.currentValue)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Equity Curve Tab */}
      {activeTab === 'equity' && (
        <div style={{ 
          padding: 24, 
          backgroundColor: 'white', 
          borderRadius: 12, 
          border: `1px solid ${branding.borderColor}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 20px 0' }}>Portfolio Value Over Time</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={analytics.equityCurve}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={(value) => new Date(value).toLocaleDateString()}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
              />
              <Tooltip 
                formatter={(value) => [formatCurrency(Number(value)), 'Portfolio Value']}
                labelFormatter={(value) => new Date(value).toLocaleString()}
              />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke={branding.primaryColor} 
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Risk Analysis Tab */}
      {activeTab === 'risk' && (
        <div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: 20, 
            marginBottom: 32 
          }}>
            <div style={{ 
              padding: 20, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Max Drawdown</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#f44336' }}>
                {formatPercentage(analytics.riskMetrics.maxDrawdownPercent)}
              </div>
            </div>
            
            <div style={{ 
              padding: 20, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Current Drawdown</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#ff9800' }}>
                {formatPercentage(analytics.riskMetrics.currentDrawdownPercent)}
              </div>
            </div>
            
            <div style={{ 
              padding: 20, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>VaR (95%)</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#f44336' }}>
                {formatPercentage(analytics.riskMetrics.var95)}
              </div>
            </div>
            
            <div style={{ 
              padding: 20, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Beta</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: branding.primaryColor }}>
                {analytics.riskMetrics.beta.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Benchmark Comparison Tab */}
      {activeTab === 'benchmark' && (
        <div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: 20, 
            marginBottom: 32 
          }}>
            <div style={{ 
              padding: 20, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Portfolio Return</h4>
              <div style={{ 
                fontSize: 24, 
                fontWeight: 600, 
                color: getPnLColor(analytics.benchmarkComparison.portfolioReturn)
              }}>
                {formatPercentage(analytics.benchmarkComparison.portfolioReturn)}
              </div>
            </div>
            
            <div style={{ 
              padding: 20, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Benchmark Return</h4>
              <div style={{ 
                fontSize: 24, 
                fontWeight: 600, 
                color: getPnLColor(analytics.benchmarkComparison.benchmarkReturn)
              }}>
                {formatPercentage(analytics.benchmarkComparison.benchmarkReturn)}
              </div>
            </div>
            
            <div style={{ 
              padding: 20, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Alpha</h4>
              <div style={{ 
                fontSize: 24, 
                fontWeight: 600, 
                color: getPnLColor(analytics.benchmarkComparison.alpha)
              }}>
                {formatPercentage(analytics.benchmarkComparison.alpha)}
              </div>
            </div>
            
            <div style={{ 
              padding: 20, 
              backgroundColor: 'white', 
              borderRadius: 12, 
              border: `1px solid ${branding.borderColor}`,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Correlation</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: branding.primaryColor }}>
                {analytics.benchmarkComparison.correlation.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Last Updated */}
      <div style={{ 
        marginTop: 32, 
        textAlign: 'center', 
        fontSize: 14, 
        color: '#666' 
      }}>
        Last updated: {new Date(analytics.lastUpdated).toLocaleString()}
      </div>
    </div>
  );
};

export default ProfitDashboard;
